import { generateShortId } from '@o2d/shared';
import { creationDefaults } from '../../core/viewport/zoom-cluster.service.js';
import type { RectangleObject } from './rectangle.interface.js';

const DEFAULT_STROKE_COLOR = '#374151';
const DEFAULT_STROKE_WIDTH = 3;
const DEFAULT_FILL = '';
const DEFAULT_CORNER_RADIUS = 0;

/**
 * Freeze the payload in DEV builds so accidental mutations surface as a
 * TypeError instead of silently corrupting the RectangleObject contract.
 */
function freezeInDev<T extends object>(value: T): T {
  if (import.meta.env?.DEV) {
    return Object.freeze(value);
  }
  return value;
}

/**
 * Stefan, 2026-09-03 — "choose properties before drawing": optional overrides
 * for the pre-placement-staged options-circle values (see
 * `stagePrePlacementProperty()` in `o2d-svg-optionsbar.element.ts`). Mirrors
 * `CreateLineDefaultsOptions` (`line-defaults.factory.ts`) — every field is
 * optional and falls back to the existing hardcoded default via `??`.
 */
export interface CreateRectangleDefaultsOptions {
  readonly strokeColor?: string;
  readonly strokeWidth?: number;
  readonly fill?: string;
  readonly cornerRadius?: number;
  /** Bugreport (Anke, 2026-09-03): "fehlen für: schatten, transparenz" — no
   *  hardcoded default (unset renders as "off", same as after drawing). */
  readonly boxShadow?: 'on';
  readonly backgroundOpacity?: number;
  readonly strokeStyle?: 'solid' | 'dashed' | 'dotted';
}

/**
 * Build a fresh rectangle default payload — extracted from the inline
 * `placeRectangleAt` body in `o2d-whiteboard.element.ts` per PRD-F1
 * §TR-Defaults-Factory Rectangle.
 *
 * - `sizeOpt === null`: bare-click branch, rectangle is centred on the pointer
 *   (top-left = point - size/2). Side length scales inversely with viewport
 *   scale via `creationDefaults`.
 * - `sizeOpt !== null`: drag-to-create branch, the dragged box is the exact
 *   placement rectangle (top-left = pointer).
 */
export function createRectangleDefaults(
  viewportScale: number,
  point: { x: number; y: number },
  sizeOpt: { width: number; height: number } | null,
  options?: CreateRectangleDefaultsOptions,
): RectangleObject {
  const cd = creationDefaults(viewportScale);
  const w = sizeOpt ? sizeOpt.width : cd.rectWidth;
  const h = sizeOpt ? sizeOpt.height : cd.rectHeight;
  const x = sizeOpt ? point.x : point.x - w / 2;
  const y = sizeOpt ? point.y : point.y - h / 2;
  const rect: RectangleObject = {
    id: generateShortId(),
    type: 'rectangle',
    x,
    y,
    width: w,
    height: h,
    rotation: 0,
    strokeColor: options?.strokeColor ?? DEFAULT_STROKE_COLOR,
    strokeWidth: options?.strokeWidth ?? DEFAULT_STROKE_WIDTH,
    fill: options?.fill ?? DEFAULT_FILL,
    cornerRadius: options?.cornerRadius ?? DEFAULT_CORNER_RADIUS,
    ...(options?.boxShadow !== undefined ? { boxShadow: options.boxShadow } : {}),
    ...(options?.backgroundOpacity !== undefined ? { backgroundOpacity: options.backgroundOpacity } : {}),
    ...(options?.strokeStyle !== undefined ? { strokeStyle: options.strokeStyle } : {}),
  };
  return freezeInDev(rect);
}
